import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import type {
  Article,
  ArticleFilters,
  ArticleFormData,
  ArticleStep,
  ArticleContent,
  KnowledgeBaseContextValue,
  HowToContent,
  KnownErrorContent,
  SOPRunbookContent,
  EmergencyContact,
} from '../types/knowledgeBase';
import { ArticleType, Department } from '../types/knowledgeBase';
import { useArticleRepository } from '../data/hooks';
import { useCurrentUser } from '../hooks/useCurrentUser';

const KnowledgeBaseContext = createContext<KnowledgeBaseContextValue | null>(null);

interface KnowledgeBaseProviderProps {
  children: ReactNode;
}

// Key for tracking viewed articles in session
const VIEWED_ARTICLES_KEY = 'kb_viewed_articles';

function getViewedArticles(): Set<string> {
  try {
    const stored = sessionStorage.getItem(VIEWED_ARTICLES_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function markArticleViewed(articleId: string): boolean {
  const viewed = getViewedArticles();
  if (viewed.has(articleId)) {
    return false; // Already viewed this session
  }
  viewed.add(articleId);
  try {
    sessionStorage.setItem(VIEWED_ARTICLES_KEY, JSON.stringify([...viewed]));
  } catch {
    // Ignore storage errors
  }
  return true; // First view this session
}

/**
 * Transform form steps to article steps with IDs
 */
function transformSteps(
  steps: Array<{
    id?: string;
    title: string;
    content: string;
    imageUrl?: string;
    imageAlt?: string;
  }>
): ArticleStep[] {
  return steps.map((step, index) => ({
    id: step.id ?? crypto.randomUUID(),
    stepNumber: index + 1,
    title: step.title,
    content: step.content,
    imageUrl: step.imageUrl,
    imageAlt: step.imageAlt,
  }));
}

/**
 * Transform form emergency contacts to entity contacts
 */
function transformEmergencyContacts(
  contacts: Array<{
    id?: string;
    name: string;
    role: string;
    contact: string;
  }>
): EmergencyContact[] {
  return contacts.map((c) => ({
    id: c.id ?? crypto.randomUUID(),
    name: c.name,
    role: c.role,
    contact: c.contact,
  }));
}

/**
 * Transform form content to article content
 */
function transformFormContent(formContent: ArticleFormData['articleContent']): ArticleContent {
  switch (formContent.type) {
    case 'how-to':
      return {
        type: 'how-to',
        content: {
          steps: transformSteps(formContent.content.steps),
        } as HowToContent,
      };
    case 'known-error':
      return {
        type: 'known-error',
        content: {
          affectedSystems: formContent.content.affectedSystems,
          symptoms: formContent.content.symptoms,
          workaround: formContent.content.workaround,
          relatedArticleIds: formContent.content.relatedArticleIds,
        } as KnownErrorContent,
      };
    case 'sop-runbook':
      return {
        type: 'sop-runbook',
        content: {
          objective: formContent.content.objective,
          prerequisites: formContent.content.prerequisites,
          procedureSteps: transformSteps(formContent.content.procedureSteps),
          verification: formContent.content.verification,
          rollbackProcedure: formContent.content.rollbackProcedure,
          emergencyContacts: transformEmergencyContacts(formContent.content.emergencyContacts),
        } as SOPRunbookContent,
      };
  }
}

export function KnowledgeBaseProvider({ children }: KnowledgeBaseProviderProps) {
  const articleRepo = useArticleRepository();
  const currentUser = useCurrentUser();

  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<ArticleFilters>({
    searchQuery: '',
    categories: [],
    articleTypes: [],
    audiences: [],
  });

  // Load articles from repository on mount
  useEffect(() => {
    let mounted = true;

    async function loadArticles() {
      try {
        const loaded = await articleRepo.getAll();
        if (mounted) {
          setArticles(loaded);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load articles:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadArticles();

    return () => {
      mounted = false;
    };
  }, [articleRepo]);

  const getArticleById = useCallback(
    (id: string): Article | undefined => {
      return articles.find((article) => article.id === id);
    },
    [articles]
  );

  const getRelatedArticles = useCallback(
    (articleIds: string[]): Article[] => {
      return articles.filter((article) => articleIds.includes(article.id));
    },
    [articles]
  );

  const createArticle = useCallback(
    async (data: ArticleFormData): Promise<Article> => {
      const now = new Date();
      const articleContent = transformFormContent(data.articleContent);

      // For backward compatibility, extract steps from how-to content
      const steps: ArticleStep[] =
        articleContent.type === 'how-to' ? articleContent.content.steps : [];

      // Create temp article for optimistic update
      const tempArticle: Article = {
        id: crypto.randomUUID(),
        title: data.title,
        description: data.description,
        category: data.category,
        tags: data.tags,
        articleType: data.articleType,
        articleContent,
        audiences: data.audiences,
        steps,
        authorId: currentUser.id,
        authorName: currentUser.name,
        createdAt: now,
        updatedAt: now,
        viewCount: 0,
        isPublished: data.isPublished,
      };

      // Optimistic update
      setArticles((prev) => [tempArticle, ...prev]);

      try {
        // Persist to database
        const persisted = await articleRepo.create({
          title: data.title,
          description: data.description,
          category: data.category,
          tags: data.tags,
          articleType: data.articleType,
          articleContent: data.articleContent,
          audiences: data.audiences,
          isPublished: data.isPublished,
          authorId: currentUser.id,
          authorName: currentUser.name,
        });

        // Replace temp article with persisted one
        setArticles((prev) => prev.map((a) => (a.id === tempArticle.id ? persisted : a)));
        return persisted;
      } catch (error) {
        console.error('Failed to persist article:', error);
        // Remove optimistic article on failure
        setArticles((prev) => prev.filter((a) => a.id !== tempArticle.id));
        throw error;
      }
    },
    [articleRepo, currentUser.id, currentUser.name]
  );

  const updateArticle = useCallback(
    async (id: string, data: ArticleFormData): Promise<Article | undefined> => {
      const existing = articles.find((a) => a.id === id);
      if (!existing) return undefined;

      const articleContent = transformFormContent(data.articleContent);
      const steps: ArticleStep[] =
        articleContent.type === 'how-to' ? articleContent.content.steps : [];

      const updatedArticle: Article = {
        ...existing,
        title: data.title,
        description: data.description,
        category: data.category,
        tags: data.tags,
        articleContent,
        audiences: data.audiences,
        steps,
        isPublished: data.isPublished,
        updatedAt: new Date(),
      };

      // Optimistic update
      setArticles((prev) => prev.map((a) => (a.id === id ? updatedArticle : a)));

      try {
        // Persist to database
        const persisted = await articleRepo.update(id, {
          title: data.title,
          description: data.description,
          category: data.category,
          tags: data.tags,
          articleContent: data.articleContent,
          audiences: data.audiences,
          isPublished: data.isPublished,
        });

        if (persisted) {
          setArticles((prev) => prev.map((a) => (a.id === id ? persisted : a)));
          return persisted;
        }
        return updatedArticle;
      } catch (error) {
        console.error('Failed to update article:', error);
        // Revert on failure
        setArticles((prev) => prev.map((a) => (a.id === id ? existing : a)));
        throw error;
      }
    },
    [articles, articleRepo]
  );

  const deleteArticle = useCallback(
    async (id: string): Promise<void> => {
      const existing = articles.find((a) => a.id === id);
      if (!existing) return;

      // Optimistic delete
      setArticles((prev) => prev.filter((article) => article.id !== id));

      try {
        await articleRepo.delete(id);
      } catch (error) {
        console.error('Failed to delete article:', error);
        // Restore on failure
        setArticles((prev) => [existing, ...prev]);
        throw error;
      }
    },
    [articles, articleRepo]
  );

  const incrementViewCount = useCallback(
    (id: string): void => {
      // Only increment if this is the first view this session
      if (!markArticleViewed(id)) {
        return;
      }

      // Optimistic update
      setArticles((prev) =>
        prev.map((article) =>
          article.id === id ? { ...article, viewCount: article.viewCount + 1 } : article
        )
      );

      // Persist to database
      articleRepo.incrementViewCount(id).catch((error) => {
        console.error('Failed to increment view count:', error);
      });
    },
    [articleRepo]
  );

  const contextValue = useMemo<KnowledgeBaseContextValue>(
    () => ({
      articles,
      filters,
      setFilters,
      getArticleById,
      getRelatedArticles,
      createArticle,
      updateArticle,
      deleteArticle,
      incrementViewCount,
      isLoading,
      userDepartment: currentUser.department,
      isAdmin: currentUser.isAdmin,
    }),
    [
      articles,
      filters,
      getArticleById,
      getRelatedArticles,
      createArticle,
      updateArticle,
      deleteArticle,
      incrementViewCount,
      isLoading,
      currentUser.department,
      currentUser.isAdmin,
    ]
  );

  // Show nothing while loading (RepositoryProvider handles the loading screen)
  if (isLoading) {
    return null;
  }

  return (
    <KnowledgeBaseContext.Provider value={contextValue}>{children}</KnowledgeBaseContext.Provider>
  );
}

export function useKnowledgeBase(): KnowledgeBaseContextValue {
  const context = useContext(KnowledgeBaseContext);
  if (!context) {
    throw new Error('useKnowledgeBase must be used within a KnowledgeBaseProvider');
  }
  return context;
}
